import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usd, pct, num } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/shell/PageHeader";
import { Panel, ProvenanceTag, TableSkeleton } from "@/components/common/primitives";
import { UploadCloud, FileSpreadsheet, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";

const OUTCOME = {
  exact: "text-emerald-600 bg-emerald-100", probable: "text-signal-blue bg-signal-blue/10",
  ambiguous: "text-amber-600 bg-amber-100", not_found: "text-ink-muted bg-surface-2", invalid: "text-red-600 bg-red-100",
};

export default function CatalogAnalyzer() {
  const [job, setJob] = useState(null);
  const [stage, setStage] = useState("upload"); // upload | mapping | processing | review

  useEffect(() => { api.catalogJob().then((r) => setJob(r.data)); }, []);

  return (
    <div className="space-y-4">
      <PageHeader title="Catalog Analyzer" description="CSV/XLSX bulk matching with mapping, validation and resumable background jobs."
        meta={<ProvenanceTag kind="fixture" />} />

      {/* stage stepper */}
      <div className="flex items-center gap-2 text-micro font-mono">
        {["upload", "mapping", "processing", "review"].map((s, i) => (
          <React.Fragment key={s}>
            <button onClick={() => setStage(s)} className={cn("px-2.5 h-7 rounded-ctrl uppercase", stage === s ? "ez-seg-active" : "ez-seg bg-surface-2 text-ink-muted")}>{i + 1}. {s}</button>
            {i < 3 && <span className="text-ink-muted">→</span>}
          </React.Fragment>
        ))}
      </div>

      {stage === "upload" && (
        <Panel className="p-10">
          <div className="border-2 border-dashed border-edge-strong rounded-panel p-10 text-center">
            <UploadCloud className="h-10 w-10 text-ink-muted mx-auto mb-3" />
            <p className="text-body font-medium text-ink-primary">Drop CSV/XLSX or click to upload</p>
            <p className="text-compact text-ink-muted mt-1">Up to 10,000 rows on the Growth plan · CSV formula-injection protected</p>
            <button onClick={() => setStage("mapping")} className="mt-4 h-9 px-4 rounded-ctrl bg-signal-blue text-white text-compact">Use demo file: wholesale_feb.xlsx</button>
          </div>
        </Panel>
      )}

      {stage === "mapping" && (
        <Panel className="p-4">
          <div className="text-stitle text-ink-primary mb-3">Map columns</div>
          <div className="grid md:grid-cols-3 gap-3">
            {[["SKU", "col A"], ["UPC/GTIN", "col B"], ["Title", "col C"], ["Cost", "col D"], ["Quantity", "col E"], ["Pack", "col F"], ["Brand", "col G"], ["Supplier", "col H"]].map(([f, c]) => (
              <label key={f} className="block"><span className="text-micro font-mono uppercase text-ink-muted">{f}</span>
                <select defaultValue={c} className="mt-1 w-full h-9 px-2 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact">
                  <option>{c}</option><option>—</option></select></label>
            ))}
          </div>
          <button onClick={() => setStage("processing")} className="mt-4 h-9 px-4 rounded-ctrl bg-signal-blue text-white text-compact">Validate & process</button>
        </Panel>
      )}

      {stage === "processing" && job && (
        <Panel className="p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-stitle text-ink-primary">Processing {job.filename}</div>
            <span className="text-micro font-mono text-ink-muted">{job.stage} · {job.elapsed}</span>
          </div>
          <div className="h-2 rounded bg-surface-2 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${(job.processed / job.total) * 100}%` }} /></div>
          <div className="text-micro font-mono text-ink-muted mt-1">{num(job.processed)} / {num(job.total)} rows · safe to navigate away, job continues</div>
          <div className="grid grid-cols-5 gap-2 mt-4">
            {[["exact", job.exact], ["probable", job.probable], ["ambiguous", job.ambiguous], ["not_found", job.not_found], ["invalid", job.invalid]].map(([k, v]) => (
              <div key={k} className="rounded-ctrl border border-edge-subtle p-2 text-center">
                <div className={cn("text-metricsm font-mono", OUTCOME[k].split(" ")[0])}>{num(v)}</div>
                <div className="text-micro font-mono uppercase text-ink-muted">{k.replace("_", " ")}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setStage("review")} className="mt-4 h-9 px-4 rounded-ctrl bg-signal-blue text-white text-compact">View review queue</button>
        </Panel>
      )}

      {stage === "review" && job && (
        <Panel className="p-0 overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b border-edge-subtle">
            <div className="text-compact font-medium flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" />Row outcomes</div>
            <div className="flex gap-1.5">
              <button onClick={() => toast.success("Qualifying rows exported (fixture)")} className="h-8 px-2.5 rounded-ctrl border border-edge-subtle text-micro flex items-center gap-1"><Download className="h-3.5 w-3.5" />Qualifying</button>
              <button onClick={() => toast("Failed rows exported (fixture)")} className="h-8 px-2.5 rounded-ctrl border border-edge-subtle text-micro flex items-center gap-1"><Download className="h-3.5 w-3.5" />Failed</button>
            </div>
          </div>
          <table className="w-full text-compact">
            <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2">
              <th className="py-2 px-3">Row</th><th className="px-3">SKU</th><th className="px-3">UPC</th><th className="px-3">Title</th><th className="px-3 text-right">Cost</th><th className="px-3 text-right">ROI</th><th className="px-3">Outcome</th>
            </tr></thead>
            <tbody>
              {job.rows.map((r) => (
                <tr key={r.row} className="border-b border-edge-subtle last:border-0 hover:bg-surface-2">
                  <td className="py-2 px-3 font-mono">{r.row}</td><td className="px-3 font-mono">{r.sku || "—"}</td>
                  <td className="px-3 font-mono">{r.upc || "—"}</td><td className="px-3">{r.title || <span className="text-red-600">empty</span>}</td>
                  <td className="px-3 text-right font-mono">{r.cost != null ? usd(r.cost) : "—"}</td>
                  <td className="px-3 text-right font-mono">{r.roi != null ? pct(r.roi) : "—"}</td>
                  <td className="px-3"><span className={cn("text-micro font-mono px-1.5 py-0.5 rounded uppercase", OUTCOME[r.outcome])}>{r.outcome.replace("_", " ")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
